import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import styled from "styled-components";
import { fetchAllUsersHolidaysInCompany, fetchAllUsersHolidaysInTeam } from "@features/holidays/services/HolidayService";
import { CompanyHolidayUsageSummary, SimpleUserWithHolidayUsage } from '@features/holidays/models/Holiday.model';
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { isCompanyAdminUser, isOfficeAdminUser, isTeamAdminUser } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment, { Moment } from "moment";
import TeamDropDownList, { ALL_TEAMS } from '@shared/ui/organisms/team-drop-down-list/TeamDropDownList';
import { SortableTable } from '@shared/ui/molecules/SortableTable';
import { selectAllTeams } from "@shared/store/config.duck";
import { DownloadHolidayReport } from "./components/DownloadHolidayReport";
import { DownloadAbsenceReport } from "./components/DownloadAbsenceReport";
import { Permission } from "@shared/models/user.models";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { BiggerThanMobileOnly, MobileOnly } from '@shared/ui/atoms/DeviceSpecific';

function calculateUsedPercentage(used: number, totalAllowance: number) {
  if (used === 0 && totalAllowance === 0) {
    return '100%';
  } else if (used > totalAllowance) {
    return '100%+';
  } else {
    return `${Math.round((used / totalAllowance) * 100)}%`
  }
}

export function HolidaysUsageReportingPage(props: Props) {
  const [visibleHolidaysList, setVisibleHolidaysList] = useState<SimpleUserWithHolidayUsage[]>([]);
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined);
  const hasSetDefaultTeam = useRef(false);
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = useMemo(() => isCompanyAdminUser(currentUser) || isTeamAdminUser(currentUser), [currentUser]);
  const teams = useSelector(selectAllTeams);
  const currentYear = selectedDate?.year();

  const teamsToShowInDropDown = useMemo(() => {
    if (currentUser) {
      if (currentUser.role === Permission.CompanyAdmin || currentUser.role === Permission.OfficeAdmin) {
        return teams;
      } else if (currentUser.role === Permission.TeamAdmin) {
        return teams.filter(t => t.id === currentUser.teamEntity?.id)
      } else {
        return [];
      }
    }
  }, [teams, currentUser])

  // Default the dropdown to the user's own team, falling back to the first team in the list.
  useEffect(() => {
    if (hasSetDefaultTeam.current) return;
    const list = teamsToShowInDropDown ?? [];
    if (list.length === 0) return;
    const usersTeamId = currentUser?.teamEntity?.id;
    const usersTeamInList = !!usersTeamId && list.some(t => t.id === usersTeamId);
    setSelectedTeamId(usersTeamInList ? usersTeamId : list[0].id);
    hasSetDefaultTeam.current = true;
  }, [teamsToShowInDropDown, currentUser]);

  const holidayUsageData = useMemo(() => {
    return visibleHolidaysList.map((user: SimpleUserWithHolidayUsage) => ({
      usedPercentage: calculateUsedPercentage(user.daysUsed, user.totalAllowance),
      user: `${user.firstName} ${user.lastName}`.trim(),
      used: user.daysUsed,
      allowance: user.totalAllowance,
      teamName: user.teamName,
    }))
  }, [visibleHolidaysList]);

  const holidayUsageColumns = useMemo(() => {
    return [
      {header: 'reports-page.holidays-report.percentage', accessorKey: 'usedPercentage', },
      {header: 'reports-page.holidays-report.user', accessorKey: 'user', },
      {header: 'reports-page.holidays-report.days-used', accessorKey: 'used', },
      {header: 'reports-page.holidays-report.allowance', accessorKey: 'allowance', },
      {header: 'reports-page.holidays-report.team-name', accessorKey: 'teamName', },
    ];
  }, []);

  const mobileHolidayUsageColumns = useMemo(() => {
    return [
      {header: 'reports-page.holidays-report.percentage', accessorKey: 'usedPercentage', },
      {header: 'reports-page.holidays-report.user', accessorKey: 'user', },
    ];
  }, []);

  useEffect(() => {
    if (isAdmin) {
      if (selectedTeamId === ALL_TEAMS) {
        fetchAllUsersHolidaysInCompany(currentYear)
          .then((holidayUsages: CompanyHolidayUsageSummary) => setVisibleHolidaysList(holidayUsages.holidayUsages));
      } else if (!!selectedTeamId) {
        fetchAllUsersHolidaysInTeam(currentYear, selectedTeamId)
          .then((holidayUsages: CompanyHolidayUsageSummary) => setVisibleHolidaysList(holidayUsages.holidayUsages));
      }
    }
  }, [isAdmin, currentYear, selectedTeamId]);

  const setDate = (date: Moment) => {
    setSelectedDate(date);
  }

  const teamSelected = (teamId: string) => {
    setSelectedTeamId(teamId);
  }

  const initialTableState = {
    sortBy: [ { id: 'user', desc: false } ]
  }

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      {isAdmin && <>

        {isTeamAdminUser(currentUser) && !currentUser?.teamEntity ? <>
          <Center>
            <FlatContentCard>
              <BodyLarge>You are not assigned to a team</BodyLarge>
              <BodyRegular>As a team admin once you're assigned to a team you will be able to see team members holiday reports.</BodyRegular>
            </FlatContentCard>
          </Center>
        </> : <>
          <ControlBar>
            <DateFlicker dateScale={DateScale.Year} updateGlobalDate={false} onChange={date => setDate(date)} />
            <TeamDropDownListContainer>
              <TeamDropDownList teams={teamsToShowInDropDown || []} selectedTeamId={selectedTeamId} includeAllTeams={isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser)} onSelect={teamSelected} />
            </TeamDropDownListContainer>
          </ControlBar>
          <div>
            {isAdmin ? <>
              {holidayUsageColumns.length > 0 && <>
                <MobileOnly>
                  <SortableTable data={holidayUsageData}
                                 columns={mobileHolidayUsageColumns}
                                 initialState={initialTableState} />
                </MobileOnly>
                <BiggerThanMobileOnly>
                  <SortableTable data={holidayUsageData}
                                 columns={holidayUsageColumns}
                                 initialState={initialTableState} />
                </BiggerThanMobileOnly>
              </>}

              <DownloadHolidayReport selectedDate={selectedDate}
                                     selectedTeamId={selectedTeamId}
                                     setDate={setDate} />

              <DownloadAbsenceReport />
            </> : <>
              <AccessRequired visible={!isAdmin} teamAdminsRequired={true} companyAdminsRequired={true} />
            </>}
          </div>
        </>}
      </>}
    </ReportingContainer>
  )
}

interface Props {
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding-top: 24px;
  @media (${tablet}) {
    padding: 24px;
  }
`

const TeamDropDownListContainer = styled.div`
  .teamDropDownList {
    margin: 0;
  }
`

const ControlBar = styled.div`
  display: flex;
  flex-direction: column;
  .weekFlicker {
    margin-bottom: 12px;
  }
  @media (${tablet}) {
    justify-content: space-between;
    flex-direction: row;
    .weekFlicker {
      margin-bottom: 0;
    }
  }
`
